import React, {useEffect, useState} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import {useNavigate, useParams} from 'react-router-dom';
import './index.scss';
import Input from '../../components/Input';
import Plus from '../../SVG/Plus';
import EquipmentTypeModal from '../../components/EquipmentTypeModal';
import {
  useGetAgencyQuery,
  useLazyGetCurrentFacilityInspectionsQuery,
  useLazyGetCurrentInspectionQuery,
  useLazyGetInspectionsByTypeQuery,
  useRefuseInspectionsMutation,
  useReorderCategoryMutation,
  useUpdateEquipmentTypeMutation,
  useUpdateFacilityInfoMutation,
} from '../../store/builder/inspections.api';
import {useDispatch, useSelector} from 'react-redux';
import {setLoader} from '../../store/builder/reducer';
import {toast} from 'react-toastify';
import log from '../../SVG/Log';
import {useErrorHandler} from '../../global/ErrorsHandler';
import CustomSelect from '../../components/CustomSelect';
import {Helmet} from 'react-helmet';
import Checkbox from '../../components/Checkbox';
import Datepicker from '../../components/Datepicker';
import moment from 'moment/moment';
import {useTranslation} from '../../hooks/useTranslation';
import {
  useIsExternalCodeRevision,
  useSignatureBaseOptions,
} from '../../hooks/useSignatureBaseOptions';

export default function EquipmentTypeEdit({editType}) {
  const {t} = useTranslation();
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [facilityEditData, setFacilityEditData] = useState({
    id: null,
    name: null,
    agency_id: null,
    external_code: null,
    inspections_enabled: false,
    custom_inspections_enabled: false,
    default_facility_inspections_enabled: false,
    last_weekly_date: null,
    last_monthly_date: null,
    last_annual_date: null,
    last_semiannual_date: null,
    last_quarterly_date: null,
  });

  const [
    getCurrentInspection,
    {
      data: inspectionCurrentData,
      isError: inspectionCurrentIsError,
      error: inspectionCurrentError,
      isSuccess: inspectionCurrentIsSuccess,
      isLoading: inspectionCurrentIsLoading,
    },
  ] = useLazyGetCurrentInspectionQuery();

  const [
    getCurrentFacilityInspection,
    {
      data: facilityInspectionCurrentData,
      isError: facilityInspectionCurrentIsError,
      error: facilityInspectionCurrentError,
      isSuccess: facilityInspectionCurrentIsSuccess,
      isLoading: facilityInspectionCurrentIsLoading,
    },
  ] = useLazyGetCurrentFacilityInspectionsQuery();

  const [
    refuseInspections,
    {
      data: refuseInspectionsData,
      isError: refuseInspectionsIsError,
      error: refuseInspectionsError,
      isSuccess: refuseInspectionsIsSuccess,
      isLoading: refuseInspectionsIsLoading,
    },
  ] = useRefuseInspectionsMutation();

  const [modalStatus, setModalStatus] = useState(false);
  const [addedInspections, setAddedInspections] = useState([]);
  const [pickedInspection, setPickedInspection] = useState(null);

  const [
    getInspection,
    {
      data: inspectionData,
      isError: inspectionIsError,
      error: inspectionError,
      isSuccess: inspectionIsSuccess,
      isLoading: inspectionIsLoading,
    },
  ] = useLazyGetInspectionsByTypeQuery();

  const [
    update,
    {
      data: updateData,
      isError: updateIsError,
      error: updateError,
      isSuccess: updateIsSuccess,
      isLoading: updateIsLoading,
    },
  ] = useUpdateEquipmentTypeMutation();

  const [
    updateFacilityInfo,
    {
      data: updateFacilityInfoData,
      isError: updateFacilityInfoIsError,
      error: updateFacilityInfoError,
      isSuccess: updateFacilityInfoIsSuccess,
      isLoading: updateFacilityInfoIsLoading,
    },
  ] = useUpdateFacilityInfoMutation();

  // `external_code` is SFS only and does not apply to a facility with a
  // `default_type`, so the field is hidden - and left out of the payload -
  // otherwise.
  const isExternalCodeRevision = useIsExternalCodeRevision();
  const showExternalCode =
    editType === 'facility' &&
    isExternalCodeRevision &&
    !facilityInspectionCurrentData?.data?.default_type;
  const [externalCodeOptions, externalCodeOptionsLoading] =
    useSignatureBaseOptions({skip: !showExternalCode});

  const [errorHandler, setErrorHandler] = useErrorHandler([
    updateFacilityInfoError,
    inspectionCurrentError,
    updateError,
    inspectionError,
    refuseInspectionsError,
    facilityInspectionCurrentError,
  ]);

  useEffect(() => {
    getInspection(editType === 'facility' ? 'facility' : 'equipment_type');
    if (editType === 'facility') {
      getCurrentFacilityInspection(params.id);
    } else {
      getCurrentInspection(params.id);
    }
  }, [facilityInspectionCurrentIsSuccess]);

  useEffect(() => {
    if (updateFacilityInfoIsSuccess) {
      toast.success(t('equipmentTypeEdit.messages.updateSuccess'));
      setTimeout(() => {
        navigate('/facility');
      }, 500);
    }
  }, [updateFacilityInfoIsSuccess, t]);

  useEffect(() => {
    if (facilityInspectionCurrentData?.data) {
      const data = facilityInspectionCurrentData.data;
      const fields = data.fields || [];
      
      // Extract date values from fields array
      const dateValues = {};
      fields.forEach(field => {
        if (field?.value) {
          // Convert the date string to MM/DD/YYYY format
          dateValues[field.key] = moment(field.value).format('MM/DD/YYYY');
        } else if (data[field?.key]) {
          // Fallback to direct data properties
          dateValues[field.key] = data[field.key];
        }
      });

      setFacilityEditData(old => ({
        ...old,
        name: data.name,
        id: data.id,
        agency_id: data.agency_id,
        external_code: data.external_code ? String(data.external_code) : null,
        inspections_enabled: data.inspections_enabled,
        custom_inspections_enabled: data.custom_inspections_enabled,
        default_facility_inspections_enabled: data.default_facility_inspections_enabled,
        ...dateValues,
        fields: fields, // Store the original fields array for rendering
      }));
    }
  }, [facilityInspectionCurrentIsSuccess]);

  useEffect(() => {
    if (inspectionIsLoading) {
      dispatch(setLoader(true));
    }
    if (inspectionIsSuccess) {
      dispatch(setLoader(false));
    }
    if (updateIsLoading) {
      dispatch(setLoader(true));
    }
    if (updateIsSuccess) {
      // toast.success(updateData.message);
      setModalStatus(false);
    }
    if (refuseInspectionsIsLoading) {
      dispatch(setLoader(true));
    }
    if (refuseInspectionsIsSuccess) {
      dispatch(setLoader(false));
      toast.success(t('equipmentTypeEdit.messages.refuseSuccess'));
    }
  }, [
    inspectionIsLoading,
    inspectionIsSuccess,
    updateIsSuccess,
    updateIsLoading,
    refuseInspectionsIsSuccess,
  ]);

  const removeFromAdded = id => {
    if (editType === 'facility') {
      refuseInspections({inspection_form_id: id, facility_id: params?.id});
    } else {
      refuseInspections({
        inspection_form_id: id,
        equipment_type_id: params?.id,
      });
    }
  };

  const saveEquipment = () => {
    if (editType === 'facility') {
      update({
        inspection_form_id: pickedInspection?.id,
        facility_id: params?.id
      })
        .unwrap()
        .then(res => {
          toast.success(t('equipmentTypeEdit.messages.updateSuccess'));
        });
      setPickedInspection(null);
    } else {
      update({
        inspection_form_id: pickedInspection?.id,
        equipment_type_id: params?.id,
      })
        .unwrap()
        .then(res => {
          toast.success(t('equipmentTypeEdit.messages.updateSuccess'));
        });
      setPickedInspection(null);
    }
  };

  const saveFacilityInfo = () => {
    // Format dates to Y-m-d format and structure as fields array
    const formattedData = { ...facilityEditData };
    
    // Create fields array with formatted date values
    const fieldsArray = [];
    if (facilityEditData.fields) {
      facilityEditData.fields.forEach(field => {
        if (field.key && formattedData[field.key]) {
          // Convert from MM/DD/YYYY to Y-m-d format
          const formattedValue = moment(formattedData[field.key], 'MM/DD/YYYY').format('YYYY-MM-DD');
          fieldsArray.push({
            key: field.key,
            value: formattedValue
          });
        }
      });
    }
    
    // Remove individual date fields from the main object and add fields array
    const { last_weekly_date, last_monthly_date, last_quarterly_date, last_semiannual_date, last_annual_date, fields, external_code, ...restData } = formattedData;
    
    const finalData = {
      ...restData,
      ...(showExternalCode ? {external_code: external_code || ''} : {}),
      fields: fieldsArray
    };
    
    updateFacilityInfo(finalData);
  };

  const handleFacilityData = e => {
    setFacilityEditData(old => ({...old, [e.target.name]: e.target.value}));
  };
  const handleFacilityEnabled = e => {
    setFacilityEditData(old => ({
      ...old,
      inspections_enabled: e.target.checked,
    }));
  };
  const handleCustomInspectionFacilityEnabled = e => {
    setFacilityEditData(old => ({
      ...old,
      custom_inspections_enabled: e.target.checked,
    }));
  };
  const handleFacilityEnabledDefault = v => {
    setFacilityEditData(old => ({
      ...old,
      default_facility_inspections_enabled: v,
    }));
  };

  return (
    <Wrapper>
      <Helmet>
        <title>
          {editType === 'facility' ? t('equipmentTypeEdit.pageTitle.editFacility') : t('equipmentTypeEdit.pageTitle.editEquipmentType')}
        </title>
      </Helmet>
      {modalStatus && (
        <EquipmentTypeModal
          exit={() => setModalStatus(false)}
          list={inspectionData.data}
          comparisonList={
            editType === 'facility'
              ? facilityInspectionCurrentData?.data?.links
              : inspectionCurrentData?.data?.links
          }
          setAddedInspections={setAddedInspections}
          addedInspections={addedInspections}
          pickedInspection={pickedInspection}
          setPickedInspection={setPickedInspection}
          saveEquipment={() => saveEquipment()}
        />
      )}
      <DashboardHeader
        title={
          editType === 'facility' ? t('equipmentTypeEdit.title.editFacility') : t('equipmentTypeEdit.title.editEquipmentType')
        }
        type={editType === 'facility' ? 'facility-edit' : 'equipment-edit'}
        action={saveFacilityInfo}
      />
      <div className='editType'>
        {editType === 'facility' ? (
          <div style={{marginBottom: 20}} className='editType__fields'>
            <Input
              containerWidth={'95%'}
              maxwidth={'100%'}
              placeholder={t('equipmentTypeEdit.labels.facilityName')}
              defaultValue={facilityInspectionCurrentData?.data?.name}
              onChange={handleFacilityData}
              name={'name'}
              id={'name'}
              label={t('equipmentTypeEdit.labels.facilityName')}
              readOnly={!facilityInspectionCurrentData?.data?.can_edit}
            />
            {showExternalCode && (
            <div style={{width: '95%', marginBottom: 20}}>
              <CustomSelect
                id='external_code'
                label={t('modal.signatureBaseId')}
                placeholder={t('modal.signatureBaseId')}
                name={'external_code'}
                options={externalCodeOptions}
                value={facilityEditData?.external_code}
                isLoading={externalCodeOptionsLoading}
                onChange={handleFacilityData}
                disabled={!facilityInspectionCurrentData?.data?.can_edit}
              />
            </div>
            )}
            {userInfo?.is_global && (
             <>
               <label className='checkbox-container'>
                 {t('equipmentTypeEdit.labels.inspectionEnabled')}
                 <input
                     type='checkbox'
                     key={facilityEditData?.inspections_enabled}
                     defaultChecked={facilityEditData?.inspections_enabled}
                     id={'inspection_enabled'}
                     name={'inspection_enabled'}
                     onChange={handleFacilityEnabled}
                 />
                 <span className='checkmark'></span>
               </label>
               <label className='checkbox-container'>
                 {t('equipmentTypeEdit.labels.customInspectionEnabled')}
                 <input
                     type='checkbox'
                     key={facilityEditData?.custom_inspections_enabled}
                     defaultChecked={facilityEditData?.custom_inspections_enabled}
                     id={'custom_inspections_enabled'}
                     name={'custom_inspections_enabled'}
                     onChange={handleCustomInspectionFacilityEnabled}
                 />
                 <span className='checkmark'></span>
               </label>
             </>
            )}

            {facilityInspectionCurrentData?.data?.can_edit && (
              <>
                <span style={{fontSize: '14px'}}>
                  {t('equipmentTypeEdit.question.fuelFacilityChecks')}
                </span>
                <div
                  className='subscription__plan-info_checkbox'
                  style={{marginTop: 10, marginBottom: 16}}
                >
                  <div className='radio-option'>
                    <input
                      type='radio'
                      name={'no'}
                      id={'no'}
                      value={'No'}
                      checked={
                        !facilityEditData?.default_facility_inspections_enabled
                      }
                      onChange={() => handleFacilityEnabledDefault(0)}
                    />
                    <label
                      style={
                        !facilityEditData?.default_facility_inspections_enabled
                          ? {color: '#4268B0'}
                          : {}
                      }
                      htmlFor={'no'}
                    >
                      {t('equipmentTypeEdit.options.no')}
                    </label>
                  </div>
                  <div className='radio-option'>
                    <input
                      type='radio'
                      name={'yes'}
                      id={'yes'}
                      checked={
                        facilityEditData?.default_facility_inspections_enabled
                      }
                      value={'Yes'}
                      onChange={() => handleFacilityEnabledDefault(1)}
                    />
                    <label
                      style={
                        facilityEditData?.default_facility_inspections_enabled
                          ? {color: '#4268B0'}
                          : {}
                      }
                      htmlFor={'yes'}
                    >
                      {t('equipmentTypeEdit.options.yes')}
                    </label>
                  </div>
                </div>
              </>
            )}

            {(userInfo?.is_global ||
              userInfo?.is_admin ||
              userInfo?.is_corporate) && (
              <>
                {facilityEditData?.fields &&
                facilityEditData?.fields?.map((field, index) => (
                    <div key={field?.key} style={{marginTop: index === 0 ? 0 : 16}}>
                      <span className='customInput' style={{marginBottom: 8}}>
                        {field?.label}
                      </span>
                      <Datepicker
                        value={
                          facilityEditData?.[field?.key]
                            ? moment(facilityEditData?.[field?.key], 'MM/DD/YYYY').toDate()
                            : null
                        }
                        setValue={e =>
                          setFacilityEditData(old => ({
                            ...old,
                            [field?.key]:
                              e === null ? null : moment(e).format('MM/DD/YYYY'),
                          }))
                        }
                      />
                    </div>
                  ))
                }
              </>
            )}
          </div>
        ) : (
          // <Input placeholder={'Equipment Type ' + params.id} value={inspectionCurrentData?.data?.title}
          //        label="Equipment Type"/>
          <div>
            <p className='editType__equipmentLabel'>{t('equipmentTypeEdit.equipmentTypeLabel')}</p>
            <div className='editType__equipmentName'>
              {inspectionCurrentData?.data?.title}
            </div>
          </div>
        )}
        <div>
          {
            // return {id: item?.form?.id, name: item?.form?.name, period: item?.form?.period}

            editType === 'facility'
              ? facilityInspectionCurrentData?.data?.links?.map(inspection => (
                  <div className='editType__added' key={inspection?.form?.id}>
                    <p>{inspection?.form?.name}</p>
                    {facilityInspectionCurrentData?.data?.can_edit && (
                      <img
                        src='/assets/icons/close.svg'
                        alt='icon'
                        onClick={() => removeFromAdded(inspection?.form?.id)}
                      />
                    )}
                  </div>
                ))
              : inspectionCurrentData?.data?.links?.map(inspection => (
                  <div className='editType__added' key={inspection?.form?.id}>
                    <p>{inspection?.form?.name}</p>
                    <img
                      src='/assets/icons/close.svg'
                      alt='icon'
                      onClick={() => removeFromAdded(inspection?.form?.id)}
                    />
                  </div>
                ))
          }
        </div>
        {facilityInspectionCurrentData?.data?.can_edit && (
          <div className='editType__btn' onClick={() => setModalStatus(true)}>
            <Plus color={'#4268B0'} />
            <span>{t('equipmentTypeEdit.buttons.addInspection')}</span>
          </div>
        )}
      </div>
    </Wrapper>
  );
}
